import { isValidUuid } from "components/admin/forms/validation";

export type CheckoutLineItem = {
  merchandise_item_id: string;
  otp_required: boolean;
  quantity: number;
};

export type CheckoutLineItemErrors = Partial<Record<"merchandise_item_id" | "quantity", string>>;

export const createCheckoutLineItem = (): CheckoutLineItem => ({
  merchandise_item_id: "",
  otp_required: false,
  quantity: 1,
});

export const appendCheckoutLineItem = (lineItems: CheckoutLineItem[]) => [
  ...lineItems,
  createCheckoutLineItem(),
];

export const removeCheckoutLineItem = (lineItems: CheckoutLineItem[], index: number) =>
  lineItems.filter((_, currentIndex) => currentIndex !== index);

export const updateCheckoutLineItem = (
  lineItems: CheckoutLineItem[],
  index: number,
  patch: Partial<CheckoutLineItem>,
) =>
  lineItems.map((lineItem, currentIndex) =>
    currentIndex === index ? { ...lineItem, ...patch } : lineItem,
  );

export const validateFollowAthleteSelection = (selectedAthleteId: string) => ({
  selectedAthleteId: selectedAthleteId ? "" : "Select an athlete to follow.",
});

export const validateQuickOrderDraft = ({
  merchandiseItemId,
  quantity,
}: {
  merchandiseItemId: string;
  quantity: number;
}) => ({
  merchandise_item_id: merchandiseItemId ? "" : "Select a merchandise item.",
  quantity:
    Number.isFinite(Number(quantity)) && Number(quantity) > 0
      ? ""
      : "Quantity must be greater than zero.",
});

export const validateCheckoutLineItem = (lineItem: CheckoutLineItem): CheckoutLineItemErrors => ({
  merchandise_item_id: lineItem.merchandise_item_id ? "" : "Select a merchandise item.",
  quantity:
    Number.isFinite(Number(lineItem.quantity)) && Number(lineItem.quantity) > 0
      ? ""
      : "Quantity must be greater than zero.",
});

export const getCheckoutLineItemErrors = (lineItems: CheckoutLineItem[]) =>
  lineItems.map(validateCheckoutLineItem);

export const getCheckoutLineItemsErrorMessage = (lineItems: CheckoutLineItem[]) => {
  if (!lineItems.length) {
    return "Add at least one checkout line item.";
  }

  const hasInvalidItem = getCheckoutLineItemErrors(lineItems).some((errors) =>
    Object.values(errors).some(Boolean),
  );

  return hasInvalidItem
    ? "Each checkout line item needs a merchandise item and a quantity above zero."
    : "";
};

export const isCheckoutLineItemsValid = (lineItems: CheckoutLineItem[]) =>
  !getCheckoutLineItemsErrorMessage(lineItems);

export const validatePaymentDraft = ({
  amount,
  targetId,
}: {
  amount: string;
  targetId: string;
}) => ({
  amount:
    !amount.trim() || (Number.isFinite(Number(amount)) && Number(amount) > 0)
      ? ""
      : "Amount override must be greater than zero.",
  target_id: targetId ? "" : "Select a target before creating a payment.",
});

export const validateRequestPurchaseDraft = ({
  requestPrice,
  requestTitle,
  selectedPlayerId,
}: {
  requestPrice: string;
  requestTitle: string;
  selectedPlayerId: string;
}) => ({
  requestPrice:
    requestPrice.trim() && Number.isFinite(Number(requestPrice)) && Number(requestPrice) > 0
      ? ""
      : "Price must be greater than zero.",
  requestTitle: requestTitle.trim() ? "" : "Request title is required.",
  selectedPlayerId: selectedPlayerId ? "" : "Select an athlete first.",
});

export const validateRequestFulfilmentDraft = (requestToFulfill: string) => ({
  requestToFulfill: requestToFulfill
    ? isValidUuid(requestToFulfill)
      ? ""
      : "Use a valid request UUID."
    : "Enter a request UUID to fulfil.",
});

export const validateWalletTopUpDraft = (amount: string) => ({
  amount:
    amount.trim() && Number.isFinite(Number(amount)) && Number(amount) > 0
      ? ""
      : "Top-up amount must be greater than zero.",
});

export const validateWalletTransferDraft = ({
  recipientUserId,
  transferAmount,
}: {
  recipientUserId: string;
  transferAmount: string;
}) => ({
  recipientUserId: recipientUserId ? "" : "Select a recipient user.",
  transferAmount:
    transferAmount.trim() && Number.isFinite(Number(transferAmount)) && Number(transferAmount) > 0
      ? ""
      : "Transfer amount must be greater than zero.",
});

export const validateTeamPlayerAssignmentDraft = ({
  selectedPlayerId,
  selectedTeamId,
}: {
  selectedPlayerId: string;
  selectedTeamId: string;
}) => ({
  selectedPlayerId: selectedPlayerId ? "" : "Select a school player.",
  selectedTeamId: selectedTeamId ? "" : "Select a team first.",
});

export const getSelectableRequestAthletes = ({
  athleteOwnedAccount,
  athletes,
  canCreateRequests,
  canManageRequests,
  currentUser,
}: {
  athleteOwnedAccount: boolean;
  athletes: any[];
  canCreateRequests: boolean;
  canManageRequests: boolean;
  currentUser: any;
}) =>
  athleteOwnedAccount && !canCreateRequests && !canManageRequests
    ? athletes.filter((athlete: any) => athlete.id === currentUser?.player?.id)
    : athletes;

export const getPaymentTargets = ({
  classes,
  merchandise,
  paymentType,
}: {
  classes: any[];
  merchandise: any[];
  paymentType: "merchandise_item_id" | "player_class_id";
}) => (paymentType === "merchandise_item_id" ? merchandise : classes);

export const getWalletRecipients = (users: any[], currentUserId?: string | number) =>
  users.filter((user: any) => String(user.id) !== String(currentUserId || ""));
